import { useId } from 'react';
import type { Garment, GarmentType } from '@/api/models';

/**
 * Illustrated garment, drawn from the product's "Rack display" settings in WooCommerce.
 * The paths, prints and shading are a direct port of the approved design.
 */

const BODY: Record<GarmentType, string> = {
  tee: 'M72 22 Q100 38 128 22 L170 38 L194 84 L163 97 L154 80 L156 214 Q100 220 44 214 L46 80 L37 97 L6 84 L30 38 Z',
  jersey: 'M74 22 L100 46 L126 22 L170 38 L194 84 L163 97 L154 80 L156 214 Q100 220 44 214 L46 80 L37 97 L6 84 L30 38 Z',
  ls: 'M72 22 Q100 38 128 22 L168 36 Q184 56 190 120 L196 198 L172 201 L158 104 L157 214 Q100 220 43 214 L42 104 L28 201 L4 198 L10 120 Q16 56 32 36 Z',
  sweat: '',
  hood: '',
  casual: '',
  formal: '',
  indo: '',
  jodh: '',
};
// Long-sleeved shapes share one outline. Jackets normally show as photos; without one they draw as a longsleeve.
(['sweat', 'hood', 'casual', 'formal', 'indo', 'jodh'] as const).forEach((t) => { BODY[t] = BODY.ls; });

const JACKETS = new Set<GarmentType>(['casual', 'formal', 'indo', 'jodh']);

const PINK = '#e8418c';

export function isDark(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  return (n >> 16) * 0.3 + ((n >> 8) & 255) * 0.59 + (n & 255) * 0.11 < 110;
}

export function shade(hex: string, amt: number) {
  const n = parseInt(hex.slice(1), 16);
  const f = (v: number) => Math.max(0, Math.min(255, Math.round(v + amt)));
  return '#' + ((1 << 24) + (f(n >> 16) << 16) + (f((n >> 8) & 255) << 8) + f(n & 255)).toString(16).slice(1);
}

const useSvgId = () => 'g' + useId().replace(/[^a-zA-Z0-9]/g, '');

interface Props {
  garment: Garment;
  face?: 'front' | 'back';
  hanger?: boolean;
}

export function GarmentSvg({ garment: g, face = 'front', hanger = true }: Props) {
  const id = useSvgId();
  const d = BODY[g.type];
  const dark = isDark(g.color);
  const line = dark ? 'rgba(255,255,255,.10)' : 'rgba(0,0,0,.10)';
  const deep = shade(g.color, -28);
  const ink = dark ? '#f1efe8' : '#2a2a28';
  const ribbed = g.type === 'sweat' || g.type === 'hood';
  const hoodPath = 'M66 28 Q60 -6 100 -8 Q140 -6 134 28 Q120 46 100 48 Q80 46 66 28 Z';
  const lines = (g.printText || '').split('\n');

  return (
    <svg viewBox="-10 -30 220 260" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`${id}s`} x1="0" x2="1">
          <stop offset="0" stopColor="#fff" stopOpacity=".16" />
          <stop offset=".45" stopColor="#fff" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity=".22" />
        </linearGradient>
        <linearGradient id={`${id}w`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#b8653f" />
          <stop offset="1" stopColor="#7d3a20" />
        </linearGradient>
        <clipPath id={`${id}c`}><path d={d} /></clipPath>
      </defs>

      {hanger && (
        <>
          <path d="M100 18 V2 Q100 -16 113 -16 Q123 -16 121 -6" fill="none" stroke="#6f6f68" strokeWidth="2.6" strokeLinecap="round" />
          <path d="M42 34 Q100 10 158 34" fill="none" stroke={`url(#${id}w)`} strokeWidth="7" strokeLinecap="round" />
        </>
      )}
      {g.type === 'hood' && face === 'back' && <path d="M66 26 Q62 -6 100 -8 Q138 -6 134 26 Z" fill={deep} />}

      <path d={d} fill={g.color} />

      <g clipPath={`url(#${id}c)`}>
        <path d="M70 70 Q76 140 66 214 M132 66 Q124 150 136 214 M100 120 Q104 170 98 214" stroke={line} strokeWidth="2" fill="none" />
        {g.type === 'jersey' && (
          <>
            <path d="M30 38 L72 26 L60 90 L37 97 Z M170 38 L128 26 L140 90 L163 97 Z" fill={PINK} opacity=".55" />
            <path d="M46 100 V214 M154 100 V214" stroke={PINK} strokeWidth="5" />
          </>
        )}
        {ribbed && (
          <>
            <rect x="30" y="200" width="140" height="16" fill={deep} opacity=".55" />
            <path d="M4 186 L28 189 M172 189 L196 186" stroke={deep} strokeWidth="14" opacity=".55" />
          </>
        )}
        {g.type === 'hood' && face === 'front' && (
          <path d="M58 150 L142 150 L152 198 L48 198 Z" fill="none" stroke={deep} strokeWidth="2.5" />
        )}

        {face === 'front' && g.frontPrint === 'logo' && (
          <text x={g.type === 'tee' ? 128 : 126} y="72" fontFamily="Archivo,Arial" fontWeight="900" fontSize="9" fill={ink} opacity=".8" textAnchor="middle" letterSpacing="1">
            {lines[0] || 'ODRA'}
          </text>
        )}
        {face === 'front' && g.frontPrint === 'gothic' && (
          <>
            <text x="100" y="104" fontFamily="UnifrakturMaguntia,serif" fontSize="40" fill="#f6f4ee" textAnchor="middle">{lines[0] || 'Fortuna'}</text>
            <text x="100" y="122" fontFamily="Archivo,Arial" fontSize="7" fill={PINK} textAnchor="middle" letterSpacing="2">{lines[1] ?? 'PORTO FC'}</text>
            <path d="M128 60 l5 -6 l5 6 l-5 6z" fill={PINK} />
          </>
        )}
        {face === 'front' && g.frontPrint === 'doodle' && (
          <>
            <g fill="none" stroke="#f1efe8" strokeWidth="2.2" strokeLinecap="round">
              <path d="M60 104 Q90 70 132 66" />
              <path d="M70 112 Q100 84 138 82" />
              <path d="M134 60 l8 6 -8 6 M140 58 l2 -6 M146 66 l6 -1" />
              <path d="M66 96 l-6 -6 M58 104 l-8 0" />
            </g>
            <text x="102" y="148" fontFamily="Caveat,cursive" fontSize="22" fill="#f1efe8" textAnchor="middle">{lines[0] || 'go far,'}</text>
            <text x="106" y="170" fontFamily="Caveat,cursive" fontSize="22" fill="#f1efe8" textAnchor="middle">{lines[1] ?? 'come back'}</text>
          </>
        )}

        {face === 'back' && g.backPrint === 'number' && (
          <>
            <text x="100" y="72" fontFamily="Yellowtail,cursive" fontSize="24" fill={PINK} textAnchor="middle">{lines[0] || 'Fortuna'}</text>
            <text x="100" y="176" fontFamily="Anton,Impact,sans-serif" fontSize="100" fill="#f6f4ee" textAnchor="middle">{g.backNumber || '15'}</text>
          </>
        )}
        {face === 'back' && g.backPrint === 'small' && (
          <text x="100" y="60" fontFamily="Caveat,cursive" fontSize="15" fill="#f1efe8" textAnchor="middle">{lines[2] || 'come back soon'}</text>
        )}
        {face === 'back' && <path d="M76 24 Q100 30 124 24" stroke={deep} strokeWidth="3" fill="none" />}

        <path d={d} fill={`url(#${id}s)`} />
      </g>

      {face === 'front' && g.type === 'jersey' && <path d="M74 22 L100 46 L126 22" stroke={PINK} strokeWidth="4" fill="none" />}
      {face === 'front' && g.type === 'hood' && (
        <>
          <path d={hoodPath} fill={g.color} />
          <path d={hoodPath} fill={`url(#${id}s)`} />
          <path d="M80 28 Q84 6 100 6 Q116 6 120 28 Q110 40 100 40 Q90 40 80 28 Z" fill={deep} />
          <path d="M90 42 L87 86 M110 42 L113 86" stroke="#f3f2ee" strokeWidth="2" strokeLinecap="round" />
        </>
      )}
      {face === 'front' && g.type !== 'jersey' && g.type !== 'hood' && (
        <path d="M72 22 Q100 38 128 22" stroke={deep} strokeWidth={g.type === 'sweat' ? 6 : 3.5} fill="none" />
      )}
    </svg>
  );
}

/** Edge-on view of a hanging garment, used for pieces at rest on the rail. */
export function GarmentSideSvg({ garment: g }: { garment: Garment }) {
  const id = useSvgId();
  if (JACKETS.has(g.type)) return <JacketSideSvg id={id} garment={g} />;
  const deep = shade(g.color, -30);
  const long = g.type !== 'tee' && g.type !== 'jersey';
  return (
    <svg viewBox="0 -30 40 260" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id} x1="0" x2="1">
          <stop offset="0" stopColor={shade(g.color, 18)} />
          <stop offset=".5" stopColor={g.color} />
          <stop offset="1" stopColor={deep} />
        </linearGradient>
      </defs>
      <path d="M20 18 V2 Q20 -16 30 -16 Q38 -16 36 -6" fill="none" stroke="#6f6f68" strokeWidth="2.6" strokeLinecap="round" />
      <rect x="15" y="15" width="10" height="9" rx="2" fill="#8c4426" />
      {g.type === 'hood' && <path d="M11 26 Q7 2 20 0 Q32 2 29 26 Z" fill={deep} />}
      <path d="M13 20 Q20 16 27 20 L30 70 L33 214 Q20 219 7 214 L10 70 Z" fill={`url(#${id})`} />
      <path d={`M26 30 Q32 ${long ? 110 : 60} 29 ${long ? 196 : 96}`} stroke="rgba(0,0,0,.18)" strokeWidth="2" fill="none" />
      {g.type === 'jersey' && <path d="M10 100 V212" stroke={PINK} strokeWidth="3" />}
    </svg>
  );
}

/**
 * Edge-on jacket for the rail, matched to the photo's colour. Indo-western pieces hang longer;
 * Jodhpuri and Indo-western pieces show a standing collar.
 */
function JacketSideSvg({ id, garment: g }: { id: string; garment: Garment }) {
  const deep = shade(g.color, -30);
  const h = g.type === 'indo' ? 226 : 212;
  const collar = g.type === 'jodh' || g.type === 'indo';
  return (
    <svg viewBox="0 -30 40 260" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id} x1="0" x2="1">
          <stop offset="0" stopColor={shade(g.color, 18)} />
          <stop offset=".5" stopColor={g.color} />
          <stop offset="1" stopColor={deep} />
        </linearGradient>
      </defs>
      <path d="M20 18 V2 Q20 -16 30 -16 Q38 -16 36 -6" fill="none" stroke="#6f6f68" strokeWidth="2.6" strokeLinecap="round" />
      <rect x="15" y="15" width="10" height="9" rx="2" fill="#8c4426" />
      <path d={`M12 22 Q20 17 28 22 L31 70 L34 ${h} Q20 ${h + 5} 6 ${h} L9 70 Z`} fill={`url(#${id})`} />
      {collar && <rect x="13" y="16" width="14" height="8" rx="2" fill={deep} />}
      <path d={`M26 30 Q32 110 29 ${h - 16}`} stroke="rgba(0,0,0,.18)" strokeWidth="2" fill="none" />
    </svg>
  );
}
